// src/components/auth/ResetPasswordForm.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle, KeyRound, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';

const ResetPasswordForm = () => {
   const navigate = useNavigate();
  const { resetToken } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [validationErrors, setValidationErrors] = useState({
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    // Validate token exists
    if (!resetToken) {
      setError('Invalid password reset link. Please request a new password reset.');
    }
  }, [resetToken]);

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    
    // Check for at least one uppercase letter
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    
    // Check for at least one lowercase letter
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    
    // Check for at least one number
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    
    // Check for at least one special character
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return 'Password must contain at least one special character';
    }
    
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Validation
    if (name === 'password') {
      const passwordError = validatePassword(value);
      setValidationErrors({
        ...validationErrors,
        password: passwordError
      });
    } else if (name === 'confirmPassword') {
      setValidationErrors({
        ...validationErrors,
        confirmPassword: value !== formData.password ? 'Passwords do not match' : ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setValidationErrors({
        ...validationErrors,
        confirmPassword: 'Passwords do not match'
      });
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setValidationErrors({
        ...validationErrors,
        password: passwordError
      });
      return;
    }

    setLoading(true);

    try {
      // Use the API endpoint directly
      const response = await api.put(`/auth/reset-password/${resetToken}`, {
        password: formData.password
      });

      if (response?.data?.status === 'success') {
        setSuccess(true);
      } else {
        throw new Error(response?.data?.message || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. The token may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex relative">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="/USIU-Library.jpg"
            alt="USIU-Africa Library"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/90 to-usiu-blue-50/95 dark:from-slate-900/95 dark:via-slate-800/90 dark:to-slate-900/95"></div>
        </div>

        <div className="flex-1 flex items-center justify-center p-8 relative z-10">
          <div className="max-w-md w-full space-y-8">
            <div className="rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4">
              <div className="flex">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800 dark:text-green-300">
                    Password reset successful
                  </h3>
                  <p className="mt-2 text-sm text-green-700 dark:text-green-400">
                    Your password has been reset successfully. You can now log in with your new password.
                  </p>
                </div>
              </div>
            </div>
            <div className="text-center">
              <Link to="/login" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-xl shadow-sm text-white bg-usiu-blue-600 hover:bg-usiu-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-usiu-blue-500">
                Go to login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex relative">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="/USIU-Library.jpg"
          alt="USIU-Africa Library"
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/90 to-usiu-blue-50/95 dark:from-slate-900/95 dark:via-slate-800/90 dark:to-slate-900/95"></div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 relative z-10">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold bg-gradient-to-r from-gray-900 to-gray-800 dark:from-usiu-gold-400 dark:to-usiu-gold-500 bg-clip-text text-transparent">
            Reset your password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-300">
            Please enter your new password below
          </p>
        </div>

        {error && (
          <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-300">{error}</h3>
              </div>
            </div>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              New Password
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <KeyRound className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              </div>
              <input
                id="password"
                name="password"
                type={passwordVisible ? "text" : "password"}
                required
                className="appearance-none rounded-xl relative block w-full px-3 py-2 pl-10 border border-gray-300 dark:border-gray-700 dark:bg-slate-900 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white focus:outline-none focus:ring-usiu-blue-500 focus:border-usiu-blue-500 focus:z-10 sm:text-sm"
                placeholder="New password"
                value={formData.password}
                onChange={handleChange}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setPasswordVisible(!passwordVisible)}
              >
                {passwordVisible ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {validationErrors.password && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {validationErrors.password}
              </p>
            )}
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Password must be at least 8 characters long and contain uppercase, lowercase, number and special character.
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Confirm New Password
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <KeyRound className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              </div>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={confirmPasswordVisible ? "text" : "password"}
                required
                className="appearance-none rounded-xl relative block w-full px-3 py-2 pl-10 border border-gray-300 dark:border-gray-700 dark:bg-slate-900 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white focus:outline-none focus:ring-usiu-blue-500 focus:border-usiu-blue-500 focus:z-10 sm:text-sm"
                placeholder="Confirm new password"
                value={formData.confirmPassword}
                onChange={handleChange}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
              >
                {confirmPasswordVisible ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {validationErrors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {validationErrors.confirmPassword}
              </p>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-usiu-blue-600 hover:bg-usiu-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-usiu-blue-500 disabled:bg-blue-300 dark:disabled:bg-blue-800"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Resetting...
                </div>
              ) : (
                'Reset Password'
              )}
            </button>
          </div>

          <div className="flex items-center justify-center">
            <Link
              to="/login"
              className="flex items-center text-sm font-medium text-usiu-blue-600 hover:text-usiu-blue-500 dark:text-usiu-gold-400 dark:hover:text-usiu-gold-300"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to login
            </Link>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
  // const navigate = useNavigate();
  // const { token } = useParams();
  // const { resetPassword } = useAuth();
  // const [loading, setLoading] = useState(false);
  // const [error, setError] = useState('');
  // const [success, setSuccess] = useState(false);
  // const [showPassword, setShowPassword] = useState(false);
  // const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // const [formData, setFormData] = useState({
  //   password: '',
  //   confirmPassword: ''
  // });

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   if (formData.password !== formData.confirmPassword) {
  //     setError('Passwords do not match');
  //     return;
  //   }

  //   setError('');
  //   setLoading(true);

  //   try {
  //     await resetPassword(token, formData.password);
  //     setSuccess(true);
  //     setTimeout(() => {
  //       navigate('/login');
  //     }, 3000);
  //   } catch (err) {
  //     setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // if (success) {
  //   return (
  //     <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
  //       <div className="max-w-md w-full space-y-8">
  //         <div className="rounded-md bg-green-50 p-4">
  //           <div className="flex">
  //             <CheckCircle className="h-5 w-5 text-green-400" />
  //             <div className="ml-3">
  //               <h3 className="text-sm font-medium text-green-800">
  //                 Password reset successful
  //               </h3>
  //               <p className="mt-2 text-sm text-green-700">
  //                 Your password has been reset. You will be redirected to login...
  //               </p>
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  // return (
  //   <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
  //     <div className="max-w-md w-full space-y-8">
  //       <div>
  //         <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
  //           Reset your password
  //         </h2>
  //         <p className="mt-2 text-center text-sm text-gray-600">
  //           Please enter your new password
  //         </p>
  //       </div>

  //       {error && (
  //         <div className="rounded-md bg-red-50 p-4">
  //           <div className="flex">
  //             <AlertCircle className="h-5 w-5 text-red-400" />
  //             <div className="ml-3">
  //               <h3 className="text-sm font-medium text-red-800">{error}</h3>
  //             </div>
  //           </div>
  //         </div>
  //       )}

  //       <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
  //         <div className="rounded-md shadow-sm -space-y-px">
  //           <div className="relative">
  //             <label htmlFor="password" className="sr-only">
  //               New Password
  //             </label>
  //             <div className="relative">
  //               <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
  //                 <Lock className="h-5 w-5 text-gray-400" />
  //               </div>
  //               <input
  //                 id="password"
  //                 name="password"
  //                 type={showPassword ? "text" : "password"}
  //                 required
  //                 className="appearance-none rounded-none relative block w-full px-3 py-2 pl-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
  //                 placeholder="New password"
  //                 value={formData.password}
  //                 onChange={(e) => setFormData({ ...formData, password: e.target.value })}
  //                 minLength={8}
  //               />
  //               <button
  //                 type="button"
  //                 className="absolute inset-y-0 right-0 pr-3 flex items-center"
  //                 onClick={() => setShowPassword(!showPassword)}
  //               >
  //                 {showPassword ? (
  //                   <EyeOff className="h-5 w-5 text-gray-400" />
  //                 ) : (
  //                   <Eye className="h-5 w-5 text-gray-400" />
  //                 )}
  //               </button>
  //             </div>
  //           </div>
  //           <div>
  //             <label htmlFor="confirm-password" className="sr-only">
  //               Confirm Password
  //             </label>
  //             <div className="relative">
  //               <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
  //                 <Lock className="h-5 w-5 text-gray-400" />
  //               </div>
  //               <input
  //                 id="confirm-password"
  //                 name="confirm-password"
  //                 type={showConfirmPassword ? "text" : "password"}
  //                 required
  //                 className="appearance-none rounded-none relative block w-full px-3 py-2 pl-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
  //                 placeholder="Confirm password"
  //                 value={formData.confirmPassword}
  //                 onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
  //                 minLength={8}
  //               />
  //               <button
  //                 type="button"
  //                 className="absolute inset-y-0 right-0 pr-3 flex items-center"
  //                 onClick={() => setShowConfirmPassword(!showConfirmPassword)}
  //               >
  //                 {showConfirmPassword ? (
  //                   <EyeOff className="h-5 w-5 text-gray-400" />
  //                 ) : (
  //                   <Eye className="h-5 w-5 text-gray-400" />
  //                 )}
  //               </button>
  //             </div>
  //           </div>
  //         </div>

  //         <div>
  //           <button
  //             type="submit"
  //             disabled={loading}
  //             className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
  //           >
  //             {loading ? (
  //               <div className="flex items-center">
  //                 <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
  //                 Resetting password...
  //               </div>
  //             ) : (
  //               'Reset password'
  //             )}
  //           </button>
  //         </div>
  //       </form>
  //     </div>
  //   </div>
  // );
};

export default ResetPasswordForm;